/**
 * 素材カタログ。
 *
 * 一覧は PostgREST から直接引く。**見える範囲は RLS が決める**ので、
 * フロント側で権限フィルタを書かない（書くと二重管理になり、必ずズレる）。
 * ダウンロードだけは権利チェックが要るので Workers API を経由する。
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  supabase,
  type Asset,
  type AssetField,
  type AssetStatus,
  type Category,
  type CategoryField,
  type Profile,
} from '../lib/supabase.ts'
import {
  getDownloadInfo,
  getThumbnailUrls,
  getProxyUrl,
  getTranscodeStatus,
  enqueueTranscode,
  moveAssetCategory,
  updateAssetAttributes,
  deleteAsset,
  extractMetadata,
  getPreviewUrls,
  createPreviewFromBox,
  createLightUploadUrl,
  putToSignedUrl,
  pruneThumbnails,
  regenerateThumbnailFromBox,
  ApiError,
  type DownloadInfo,
} from '../lib/api.ts'
import { generateThumbnail, type Thumbnail } from '../lib/thumbnail.ts'
import {
  SidebarPanel,
  SidebarSection,
  SidebarItem,
  SidebarCheckItem,
  SidebarTree,
  GearIcon,
  useSidebarSections,
  useSidebarLayout,
  type SidebarTreeNode,
} from '../components/Sidebar.tsx'
import {
  buildCategoryTree,
  descendantIds,
  leafCategories,
  categoryPathLabel,
  type CategoryNode,
} from '../lib/categories.ts'
import {
  effectiveFields,
  fieldsForSubtree,
  facetValues,
  matchesFacet,
  formatFieldValue,
  type EffectiveField,
} from '../lib/fields.ts'
import { Lightbox } from '../components/Lightbox.tsx'
import {
  describeTranscode,
  hasActiveJobs,
  type TranscodeJob,
} from '../lib/transcodeStatus.ts'
import { formatBytes, formatDuration, STATUS_LABEL, KIND_LABEL } from '../lib/format.ts'
import { can, isStaffRole } from '../lib/roles.ts'
import { usePersistentState } from '../lib/usePersistentState.ts'
import { useFlip } from '../lib/useFlip.ts'
import { AssetThumb } from '../components/AssetThumb.tsx'
import { AssetList, ListFieldSettings, defaultListFields } from '../components/AssetList.tsx'
import { Slideshow } from '../components/Slideshow.tsx'
import { TabbedPanel, type TabDef } from '../components/TabbedPanel.tsx'

/**
 * カタログの表示モード。
 * - tile: サムネイルのタイル。サイズはスライダーで変えられる
 * - list: 縦一列＋メタデータ。項目は「表示項目」で選べる
 * - slide: スライドショー＋フィルムストリップ
 */
type ViewMode = 'tile' | 'list' | 'slide'

/** 動画プレイヤーの表示サイズ。全画面はネイティブに任せるので状態に持たない。 */
type PlayerSize = 'normal' | 'theater'

/** タイルの最小幅（px）。スライダーの範囲。 */
const TILE_MIN = 140
const TILE_MAX = 420
const TILE_DEFAULT = 240

const DENIAL_MESSAGE: Record<string, string> = {
  master_download_not_allowed: 'このライセンスでは元本をダウンロードできません（視聴のみ）',
  no_effective_license: '有効なライセンスがありません',
  download_quota_exceeded: 'ダウンロード回数の上限に達しています',
  organization_suspended: '組織の契約が停止されています',
  asset_has_no_master: '元本がまだ登録されていません',
  file_not_available: 'Box 上でファイルを参照できません',
}

export function Catalog({
  profile,
  onOpenSettings,
}: {
  profile: Profile
  /** 設定ページを開く。権限が無いユーザーには渡さない（渡さなければ歯車も出ない）。 */
  onOpenSettings?: () => void
}) {
  const [assets, setAssets] = useState<Asset[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [thumbs, setThumbs] = useState<Record<string, string>>({})
  // 'all' | 'none'（未分類）| カテゴリ id。枝を選ぶと配下すべてが対象
  const [categoryId, setCategoryId] = useState<string>('all')
  // カスタム項目の台帳と割り当て。表示・絞り込み・編集で使う
  const [fields, setFields] = useState<AssetField[]>([])
  const [assignments, setAssignments] = useState<CategoryField[]>([])
  // 項目での絞り込み（key → 選んだ値）。同じ項目の中は OR、項目どうしは AND
  const [facets, setFacets] = useState<Record<string, string[]>>({})
  // 木の開閉。無ければ開いている扱い。保存値は形を確かめてから使う
  const [treeOpenRaw, setTreeOpen] = usePersistentState<Record<string, boolean>>('catalog.tree-open', {})
  const treeOpen = useMemo(
    () => (treeOpenRaw && typeof treeOpenRaw === 'object' && !Array.isArray(treeOpenRaw) ? treeOpenRaw : {}),
    [treeOpenRaw],
  )
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Asset | null>(null)
  // 変換を投入したら、待たずに状況の取得をやり直すための世代番号
  const [pollRevision, setPollRevision] = useState(0)
  // 署名付きURLは取れたのに実体が無いサムネイル。
  // R2 の実体が消えている場合にここへ入る（過去の掃除処理の不備で発生した）。
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, true>>({})
  // 修復を試みた素材。同じ素材で何度も走らせない。
  const repairing = useRef<Set<string>>(new Set())
  const [statusFilter, setStatusFilter] = useState<'all' | AssetStatus>('all')
  // 種別はチェックボックス。両方オンが既定で、外した種別を隠す。
  const [kinds, setKinds] = useState<{ video: boolean; image: boolean }>({
    video: true,
    image: true,
  })
  const { open, toggle } = useSidebarSections({ categories: true, facets: true, status: true, kind: false })
  const sidebar = useSidebarLayout()

  // 表示モードと見た目の設定。毎回選び直させない（localStorage に保存）。
  // 保存値は型の保証が無いので、使う前に必ず既知の値へ丸める。
  const [viewModeRaw, setViewMode] = usePersistentState<ViewMode>('catalog.view-mode', 'tile')
  const viewMode: ViewMode =
    viewModeRaw === 'list' || viewModeRaw === 'slide' ? viewModeRaw : 'tile'
  const [tileSizeRaw, setTileSize] = usePersistentState<number>('catalog.tile-size', TILE_DEFAULT)
  const tileSize =
    typeof tileSizeRaw === 'number' && Number.isFinite(tileSizeRaw)
      ? Math.min(TILE_MAX, Math.max(TILE_MIN, tileSizeRaw))
      : TILE_DEFAULT
  const [listFieldsRaw, setListFields] = usePersistentState<Record<string, boolean>>(
    'catalog.list-fields',
    {},
  )
  const listFields = useMemo(
    () => ({ ...defaultListFields(), ...(listFieldsRaw ?? {}) }),
    [listFieldsRaw],
  )

  // タイルサイズの変更を滑らかに見せる。
  // 列数が変わる瞬間にタイル幅が跳ぶので、前後の矩形差を transform で補間する。
  const gridRef = useRef<HTMLUListElement | null>(null)
  const flip = useFlip(gridRef)
  useLayoutEffect(() => {
    flip.play()
  }, [tileSize, flip])

  // sticky なツールバーの高さを .catalog に伝える（scroll-padding-top に使う）。
  // 無いと、キーボードで合わせたタイルや scrollIntoView の着地位置がツールバーの下に潜る。
  // ツールバーは折り返しで高さが変わるので固定値にせず測る。
  const catalogRef = useRef<HTMLDivElement | null>(null)
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const toolbar = toolbarRef.current
    const pane = catalogRef.current
    if (!toolbar || !pane || typeof ResizeObserver === 'undefined') return
    const apply = () => pane.style.setProperty('--toolbar-h', `${toolbar.offsetHeight}px`)
    apply()
    const observer = new ResizeObserver(apply)
    observer.observe(toolbar)
    return () => observer.disconnect()
    // 読み込み中は早期 return で DOM が無い。読み込みが終わってから付け直す
  }, [loading])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [assetRes, catRes, fieldRes, assignRes] = await Promise.all([
        supabase
          .from('assets')
          .select('*')
          .order('published_at', { ascending: false, nullsFirst: false }),
        supabase.from('categories').select('*').order('sort_order'),
        supabase.from('asset_fields').select('*').order('sort_order'),
        supabase.from('category_fields').select('*'),
      ])
      if (cancelled) return
      if (assetRes.error) setError(assetRes.error.message)
      const rows = (assetRes.data ?? []) as Asset[]
      setAssets(rows)
      setCategories((catRes.data ?? []) as Category[])
      setFields((fieldRes.data ?? []) as AssetField[])
      setAssignments((assignRes.data ?? []) as CategoryField[])
      setLoading(false)

      // サムネイルは非公開バケットなので、署名付きURLをまとめて取る
      const withThumbs = rows.filter((a) => a.thumbnail_r2_key).map((a) => a.id)
      if (withThumbs.length > 0) {
        try {
          const { urls } = await getThumbnailUrls(withThumbs)
          if (!cancelled) setThumbs(urls)
        } catch {
          // サムネが出ないだけなのでカタログ自体は表示を続ける
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * 指定した素材だけを引き直す。
   *
   * 変換が終わっても assets はマウント時の内容のままなので、
   * proxy_r2_key が null に見え続ける。**そのままだと「完了（反映待ち）」で
   * 画面が固まり、再読み込みするまで再生できない。**
   * ポーリングが完了を検知した時点でここを呼ぶ。
   */
  const refreshAssets = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return
    const { data } = await supabase.from('assets').select('*').in('id', ids)
    const rows = (data ?? []) as Asset[]
    if (rows.length === 0) return
    const byId = new Map(rows.map((r) => [r.id, r]))
    setAssets((prev) => prev.map((a) => byId.get(a.id) ?? a))
    // 詳細を開いたままでも即座に再生できるようにする
    setSelected((prev) => (prev && byId.has(prev.id) ? byId.get(prev.id)! : prev))
  }, [])

  // カテゴリの木と、選択中の部分木（'all' / 'none' なら null）
  const tree = useMemo(() => buildCategoryTree(categories), [categories])
  const subtree = useMemo(
    () => (categoryId === 'all' || categoryId === 'none' ? null : descendantIds(categories, categoryId)),
    [categories, categoryId],
  )

  // 絞り込みに使える項目: 選択中の部分木のどこかで有効なもの
  const facetFields = useMemo(
    () =>
      categoryId === 'none'
        ? []
        : fieldsForSubtree(categoryId === 'all' ? 'all' : categoryId, categories, fields, assignments),
    [categoryId, categories, fields, assignments],
  )
  // カテゴリを変えて使えなくなった項目の条件は捨てる（見えない条件で件数が減るのを防ぐ）
  const facetKeys = facetFields.map((f) => f.key).join(',')
  useEffect(() => {
    const allowed = new Set(facetKeys.split(',').filter(Boolean))
    setFacets((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([k]) => allowed.has(k)))
      return Object.keys(next).length === Object.keys(prev).length ? prev : next
    })
  }, [facetKeys])

  // 自由文検索の対象になる項目の値（表示名に直したもの）。素材ごとに1回だけ計算する
  const attrTextById = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of assets) {
      m.set(
        a.id,
        effectiveFields(a.category_id, categories, fields, assignments)
          .map((f) => formatFieldValue(f, a.attributes?.[f.key]))
          .filter((v): v is string => v !== null)
          .join(' ')
          .toLowerCase(),
      )
    }
    return m
  }, [assets, categories, fields, assignments])

  // 項目以外の条件（状態・種別・カテゴリ・検索語）
  const passesBase = useCallback(
    (a: Asset) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false
      if (!kinds[a.kind === 'video' ? 'video' : 'image']) return false
      if (categoryId === 'none') {
        if (a.category_id !== null) return false
      } else if (subtree && (!a.category_id || !subtree.has(a.category_id))) {
        return false
      }
      const q = query.trim().toLowerCase()
      if (!q) return true
      return (
        a.title.toLowerCase().includes(q) ||
        a.tags.some((t) => t.toLowerCase().includes(q)) ||
        (a.file_name ?? '').toLowerCase().includes(q) ||
        (attrTextById.get(a.id) ?? '').includes(q)
      )
    },
    [statusFilter, kinds, categoryId, subtree, query, attrTextById],
  )

  // 項目の条件。except を渡すとその項目だけ除いて判定する（値ごとの件数表示用）
  const passesFacets = useCallback(
    (a: Asset, except?: string) => {
      for (const [key, selected] of Object.entries(facets)) {
        if (key === except || selected.length === 0) continue
        const field = fields.find((f) => f.key === key)
        if (!field) continue
        if (!matchesFacet(field, a.attributes?.[key], new Set(selected))) return false
      }
      return true
    },
    [facets, fields],
  )

  const filtered = useMemo(
    () => assets.filter((a) => passesBase(a) && passesFacets(a)),
    [assets, passesBase, passesFacets],
  )

  // 項目ごとの候補（その項目以外の条件を通った素材）。値の件数はここから数える
  const facetCandidates = useMemo(
    () =>
      Object.fromEntries(
        facetFields.map((f) => [f.key, assets.filter((a) => passesBase(a) && passesFacets(a, f.key))]),
      ) as Record<string, Asset[]>,
    [facetFields, assets, passesBase, passesFacets],
  )
  const activeFacetCount = Object.values(facets).reduce((n, v) => n + v.length, 0)

  const toggleFacet = (key: string, value: string, on: boolean) =>
    setFacets((prev) => {
      const cur = prev[key] ?? []
      const next = on ? (cur.includes(value) ? cur : [...cur, value]) : cur.filter((v) => v !== value)
      return { ...prev, [key]: next }
    })

  // 木の各ノードの件数（配下を含む）
  const treeNodes = useMemo(() => {
    const direct = new Map<string, number>()
    for (const a of assets) {
      if (a.category_id) direct.set(a.category_id, (direct.get(a.category_id) ?? 0) + 1)
    }
    const toNode = (n: CategoryNode): SidebarTreeNode => {
      const children = n.children.map(toNode)
      return {
        id: n.id,
        label: n.name,
        count: (direct.get(n.id) ?? 0) + children.reduce((sum, c) => sum + (c.count ?? 0), 0),
        children,
      }
    }
    return tree.map(toNode)
  }, [tree, assets])
  const uncategorizedCount = useMemo(() => assets.filter((a) => !a.category_id).length, [assets])

  // 対象は「動画で、まだプロキシが無いもの」。
  //
  // **filtered ではなく assets から作る。** filtered にすると検索欄の1文字ごとに
  // 対象が変わってポーリングが再起動してしまう。状況は絞り込みと無関係なので
  // ここは安定させる。件数が多い場合は api 側が分割して問い合わせる。
  const pendingIds = useMemo(
    () => assets.filter((a) => a.kind === 'video' && !a.proxy_r2_key).map((a) => a.id),
    [assets],
  )
  // エンドポイントは運営スタッフ（content.write）限定。顧客ユーザーには 403 になるので呼ばない。
  const isStaff = can(profile.role, 'content.write')
  const jobs = useTranscodeStatus(pendingIds, isStaff, refreshAssets, pollRevision)

  /**
   * 実体の無いサムネイルを作り直す。
   *
   * メタデータ抽出と同じ経路で作れる（元本のダウンロードを共有できる）。
   * スタッフだけが実行できるので、顧客の画面では壊れた表示のまま
   * 「未生成」に落ちる。同じ素材を何度も走らせないよう1回に絞る。
   */
  const repairThumbnail = useCallback(
    async (assetId: string) => {
      if (!isStaff || repairing.current.has(assetId)) return
      repairing.current.add(assetId)
      try {
        await extractMetadata(assetId)
        const { urls } = await getThumbnailUrls([assetId])
        if (urls[assetId]) {
          setThumbs((prev) => ({ ...prev, [assetId]: urls[assetId]! }))
          setBrokenThumbs((prev) => {
            const next = { ...prev }
            delete next[assetId]
            return next
          })
        }
      } catch {
        // 作れない形式もある。「未生成」の表示のまま続ける。
      }
    },
    [isStaff],
  )

  /** 変換を投入して、待たずに状況を取り直す。 */
  const runTranscode = useCallback(async (assetIds: string[], resetAttempts = false) => {
    await enqueueTranscode({ assetIds, resetAttempts })
    setPollRevision((n) => n + 1)
  }, [])

  /**
   * URL は発行できたのに実体が無いサムネイルの届け先。
   * どのビューで気づいても同じ経路で「壊れた」印を付け、スタッフなら修復を試みる。
   */
  const markBroken = useCallback(
    (assetId: string) => {
      setBrokenThumbs((prev) => (prev[assetId] ? prev : { ...prev, [assetId]: true }))
      void repairThumbnail(assetId)
    },
    [repairThumbnail],
  )

  const thumbPlaceholder = useCallback(
    (asset: Asset): ReactNode =>
      brokenThumbs[asset.id] && isStaff ? 'サムネイル再生成中…' : 'サムネイル未生成',
    [brokenThumbs, isStaff],
  )

  /**
   * スライドショー用の高解像度プレビューURL。
   *
   * 同じ素材で何度も発行しない（署名付きURLの発行はAPI呼び出しなので、
   * スライドを行き来するたびに叩くとすぐ数百リクエストになる）。
   * 失敗はキャッシュしない — 次に表示したときに再試行できる。
   */
  const previewCache = useRef<Map<string, Promise<string | null>>>(new Map())
  const loadSlidePreview = useCallback(async (asset: Asset): Promise<string | null> => {
    if (asset.kind !== 'image' || !asset.poster_r2_key) return null
    const cached = previewCache.current.get(asset.id)
    if (cached) return cached
    const promise = getPreviewUrls([asset.id])
      .then((r) => r.urls[asset.id] ?? null)
      .catch(() => {
        previewCache.current.delete(asset.id)
        return null
      })
    previewCache.current.set(asset.id, promise)
    return promise
  }, [])

  if (loading) return <p className="muted pad">読み込み中…</p>

  const countBy = <T,>(pick: (a: Asset) => T, value: T) =>
    assets.filter((a) => pick(a) === value).length

  return (
    <div className="catalog-layout">
      {/* アコーディオン式サイドバー。節を足すだけで絞り込みを増やせる。
          パネル自体も畳める・幅を変えられる。下部の歯車が設定への入口（慣例の位置）。 */}
      <SidebarPanel
        collapsed={sidebar.collapsed}
        width={sidebar.width}
        onToggle={sidebar.toggleCollapsed}
        onResize={sidebar.setWidth}
        footer={
          onOpenSettings && (
            <button type="button" className="side-foot-item" onClick={onOpenSettings} title="設定">
              <GearIcon />
              <span className="side-foot-label">設定</span>
            </button>
          )
        }
      >
        <SidebarSection
          id="categories"
          title="カテゴリ"
          badge={assets.length}
          open={open.categories ?? true}
          onToggle={toggle}
        >
          <SidebarItem
            active={categoryId === 'all'}
            onClick={() => setCategoryId('all')}
            count={assets.length}
          >
            すべて
          </SidebarItem>
          {/* 階層（最大3段）。枝を選ぶと配下すべてが対象になる */}
          <SidebarTree
            nodes={treeNodes}
            activeId={categoryId}
            onSelect={setCategoryId}
            expanded={treeOpen}
            onToggleExpand={(id) => setTreeOpen({ ...treeOpen, [id]: !(treeOpen[id] ?? true) })}
          />
          {/* 未分類: 枝のフォルダに置かれたファイル等。末端へ移すまでの受け皿 */}
          {uncategorizedCount > 0 && (
            <SidebarItem
              active={categoryId === 'none'}
              onClick={() => setCategoryId('none')}
              count={uncategorizedCount}
            >
              未分類
            </SidebarItem>
          )}
        </SidebarSection>

        {/* 公開状態の絞り込みは運営側だけ。顧客に見える範囲は RLS が決めるので
            （公開中＋ライセンスのある提供終了分）、顧客には節ごと出さない。 */}
        {isStaffRole(profile.role) && (
          <SidebarSection
            id="status"
            title="公開状態"
            open={open.status ?? true}
            onToggle={toggle}
          >
            <SidebarItem active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
              すべて
            </SidebarItem>
            {(['published', 'processing', 'draft', 'archived', 'failed'] as AssetStatus[]).map((st) => (
              <SidebarItem
                key={st}
                active={statusFilter === st}
                onClick={() => setStatusFilter(st)}
                count={countBy((a) => a.status, st)}
              >
                {STATUS_LABEL[st] ?? st}
              </SidebarItem>
            ))}
          </SidebarSection>
        )}

        <SidebarSection id="kind" title="種別" open={open.kind ?? false} onToggle={toggle}>
          <SidebarCheckItem
            checked={kinds.video}
            onChange={(v) => setKinds((k) => ({ ...k, video: v }))}
            count={countBy((a) => a.kind, 'video')}
          >
            動画
          </SidebarCheckItem>
          <SidebarCheckItem
            checked={kinds.image}
            onChange={(v) => setKinds((k) => ({ ...k, image: v }))}
            count={countBy((a) => a.kind, 'image')}
          >
            写真
          </SidebarCheckItem>
        </SidebarSection>

        {/* カスタム項目での絞り込み。選んだカテゴリの部分木で有効な項目だけ出す */}
        {facetFields.length > 0 && (
          <SidebarSection
            id="facets"
            title="項目で絞り込む"
            badge={activeFacetCount > 0 ? activeFacetCount : undefined}
            open={open.facets ?? true}
            onToggle={toggle}
          >
            {facetFields.map((f) => (
              <FacetGroup
                key={f.key}
                field={f}
                values={facetValues(f, facetCandidates[f.key] ?? [])}
                selected={facets[f.key] ?? []}
                onToggle={(value, on) => toggleFacet(f.key, value, on)}
              />
            ))}
            {activeFacetCount > 0 && (
              <button type="button" className="link facet-clear" onClick={() => setFacets({})}>
                項目の絞り込みを解除
              </button>
            )}
          </SidebarSection>
        )}
      </SidebarPanel>

      <div className="catalog" ref={catalogRef}>
        <div className="toolbar" ref={toolbarRef}>
          <input
            type="search"
            placeholder="タイトル・タグ・項目・ファイル名で検索"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="muted small">{filtered.length} 件</span>

          <div className="toolbar-tools">
            {/* モードごとの道具。タイルはサイズ、リストは項目の選択。 */}
            {viewMode === 'tile' && (
              <label className="tile-size" title="サムネールの大きさ">
                <span className="tile-size-label">サイズ</span>
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <rect x="2" y="2" width="5.2" height="5.2" rx="1" />
                  <rect x="8.8" y="2" width="5.2" height="5.2" rx="1" />
                  <rect x="2" y="8.8" width="5.2" height="5.2" rx="1" />
                  <rect x="8.8" y="8.8" width="5.2" height="5.2" rx="1" />
                </svg>
                <input
                  type="range"
                  min={TILE_MIN}
                  max={TILE_MAX}
                  step={1}
                  value={tileSize}
                  // Safari は range の見た目を自前で描く必要がある（accent-color が効かない）。
                  // 塗りつぶし位置を CSS 変数で渡し、トラックの左側を accent で塗る。
                  style={
                    {
                      '--pct': `${((tileSize - TILE_MIN) / (TILE_MAX - TILE_MIN)) * 100}%`,
                    } as CSSProperties
                  }
                  onChange={(e) => {
                    // 変更前の配置を控えてから更新する（useLayoutEffect 側で差分を再生）
                    flip.snapshot()
                    setTileSize(Number(e.target.value))
                  }}
                  aria-label="サムネールサイズ"
                />
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <rect x="2" y="2" width="12" height="12" rx="1.5" />
                </svg>
              </label>
            )}
            {viewMode === 'list' && (
              <ListFieldSettings visible={listFields} onChange={setListFields} />
            )}
            <ViewSwitch mode={viewMode} onChange={setViewMode} />
          </div>
        </div>

      {error && <p className="error pad">{error}</p>}

      {filtered.length === 0 ? (
        <p className="muted pad">該当する素材がありません。</p>
      ) : viewMode === 'list' ? (
        <AssetList
          assets={filtered}
          categories={categories}
          fields={fields}
          assignments={assignments}
          visible={listFields}
          thumbs={thumbs}
          broken={brokenThumbs}
          placeholder={thumbPlaceholder}
          onError={markBroken}
          onOpen={setSelected}
          extra={
            isStaff
              ? (asset) =>
                  asset.kind === 'video' ? (
                    <TranscodeStatus
                      job={jobs[asset.id] ?? null}
                      hasProxy={!!asset.proxy_r2_key}
                      compact
                    />
                  ) : null
              : undefined
          }
        />
      ) : viewMode === 'slide' ? (
        <Slideshow
          assets={filtered}
          thumbs={thumbs}
          broken={brokenThumbs}
          placeholder={thumbPlaceholder}
          onError={markBroken}
          onOpen={setSelected}
          loadPreview={loadSlidePreview}
          keyboardEnabled={!selected}
        />
      ) : (
        <ul className="grid" ref={gridRef} style={{ '--tile-min': `${tileSize}px` } as CSSProperties}>
          {filtered.map((asset) => (
            <li key={asset.id}>
              <button className="card" onClick={() => setSelected(asset)}>
                <AssetThumb
                  asset={asset}
                  src={thumbs[asset.id] ?? null}
                  broken={brokenThumbs[asset.id]}
                  placeholder={thumbPlaceholder(asset)}
                  // URL は発行できても R2 に実体が無いことがある。
                  // 壊れた画像アイコンのまま放置せず、状態として拾う。
                  onError={() => markBroken(asset.id)}
                >
                  {asset.status !== 'published' && (
                    <span className={`badge badge-${asset.status}`}>
                      {STATUS_LABEL[asset.status] ?? asset.status}
                    </span>
                  )}
                </AssetThumb>
                {/* タイルの文字はファイル名とサイズだけに絞る。
                    タイトル等の詳しい情報はリスト表示と詳細モーダルの仕事。 */}
                <div className="card-body">
                  <h3 className="card-name" title={asset.file_name ?? asset.title}>
                    {asset.file_name ?? asset.title}
                  </h3>
                  <p className="muted card-sub">{formatBytes(asset.byte_size)}</p>
                </div>
              </button>
              {/* button の中に入れないこと。
                  3秒ごとに変わる進捗文がカードのアクセシブル名に混ざってしまう。 */}
              {isStaff && asset.kind === 'video' && (
                <TranscodeStatus
                  job={jobs[asset.id] ?? null}
                  hasProxy={!!asset.proxy_r2_key}
                  compact
                />
              )}
            </li>
          ))}
        </ul>
      )}

      </div>

      {selected && (
        <AssetDetail
          asset={selected}
          job={jobs[selected.id] ?? null}
          onTranscode={runTranscode}
          categories={categories}
          fields={fields}
          assignments={assignments}
          onDeleted={(assetId) => {
            setAssets((prev) => prev.filter((a) => a.id !== assetId))
            setSelected(null)
          }}
          profile={profile}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            setAssets((prev) => prev.map((a) => (a.id === updated.id ? updated : a)))
            // 通信中に閉じた／別の素材を開いた場合、完了時の反映でモーダルを開き直さない
            setSelected((prev) => (prev && prev.id === updated.id ? updated : prev))
          }}
          onThumbnailUpdated={async (assetId) => {
            // 旧ポスターは R2 から消えているので、スライドショーのキャッシュも捨てる
            previewCache.current.delete(assetId)
            try {
              const { urls } = await getThumbnailUrls([assetId])
              setThumbs((prev) => ({ ...prev, ...urls }))
            } catch {
              // 表示が古いままになるだけなので握りつぶす
            }
          }}
        />
      )}
    </div>
  )
}

/** 表示モードの切り替え。アイコン＋短いラベルのセグメント。 */
function ViewSwitch({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const items: { key: ViewMode; label: string; icon: ReactNode }[] = [
    {
      key: 'tile',
      label: 'タイル',
      icon: (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="1.5" y="1.5" width="5.6" height="5.6" rx="1" />
          <rect x="8.9" y="1.5" width="5.6" height="5.6" rx="1" />
          <rect x="1.5" y="8.9" width="5.6" height="5.6" rx="1" />
          <rect x="8.9" y="8.9" width="5.6" height="5.6" rx="1" />
        </svg>
      ),
    },
    {
      key: 'list',
      label: 'リスト',
      icon: (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <rect x="1.5" y="2" width="4.6" height="4.6" rx="1" />
          <rect x="7.7" y="2.6" width="6.8" height="1.3" rx=".65" />
          <rect x="7.7" y="5" width="4.8" height="1.3" rx=".65" />
          <rect x="1.5" y="9.4" width="4.6" height="4.6" rx="1" />
          <rect x="7.7" y="10" width="6.8" height="1.3" rx=".65" />
          <rect x="7.7" y="12.4" width="4.8" height="1.3" rx=".65" />
        </svg>
      ),
    },
    {
      key: 'slide',
      label: 'スライド',
      icon: (
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M3 2.5h10A1.5 1.5 0 0 1 14.5 4v6a1.5 1.5 0 0 1-1.5 1.5H3A1.5 1.5 0 0 1 1.5 10V4A1.5 1.5 0 0 1 3 2.5zm3.7 2.4v4.2L10.6 7 6.7 4.9z"
          />
          <rect x="4" y="13" width="8" height="1.4" rx=".7" />
        </svg>
      ),
    },
  ]
  return (
    <div className="view-switch" role="group" aria-label="表示モード">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={mode === it.key ? 'active' : undefined}
          aria-pressed={mode === it.key}
          title={it.label}
          onClick={() => onChange(it.key)}
        >
          {it.icon}
          <span className="vs-label">{it.label}</span>
        </button>
      ))}
    </div>
  )
}

/** 写真の詳細表示用プレビューを作り直す。ffmpeg が読めない形式は Box の representations で補う。 */
async function rebuildImagePreview(assetId: string): Promise<void> {
  const res = await extractMetadata(assetId)
  if (res.preview?.error) {
    // Box は PSD や RAW を扱えるので、失敗の仕方が補い合う
    await createPreviewFromBox(assetId)
  }
}

const describePreviewError = (e: unknown): string => {
  const code = e instanceof ApiError ? e.code : String(e)
  return code.startsWith('box_preview_unavailable')
    ? 'この形式ではプレビューを作れません（RAW など）'
    : `プレビューを作成できませんでした（${code}）`
}

/** タグ欄の文字列を配列にする。「、」「,」「，」改行で区切り、空と重複を除く。 */
const parseTags = (text: string): string[] => {
  const out: string[] = []
  for (const raw of text.split(/[、,，\n]/)) {
    const t = raw.trim()
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/**
 * 素材の詳細。サムネイルをクリックすると開く。
 *
 * 機能が増えて1枚に収まらなくなったので、タブで分ける:
 * - コンテンツ（全員）: プレビュー・基本情報・項目・ダウンロード
 * - 編集（編集者以上）: タイトル／説明／タグ・カテゴリ・カスタム項目
 * - 処理と公開（編集者以上）: メタデータ・派生物の作り直し・公開／停止／削除
 * 素材を切り替えるたびに「コンテンツ」から開く（TabbedPanel を key で作り直す）。
 *
 * 動画は［標準／拡大／全画面］の3段階。拡大はモーダルごと広げる中間サイズで、
 * 全画面はブラウザのネイティブに任せる。
 */
export function AssetDetail({
  asset,
  job,
  onTranscode,
  categories,
  fields,
  assignments,
  onDeleted,
  profile,
  onClose,
  onChanged,
  onThumbnailUpdated,
}: {
  asset: Asset
  job: TranscodeJob | null
  onTranscode: (assetIds: string[], resetAttempts?: boolean) => Promise<void>
  categories: Category[]
  fields: AssetField[]
  assignments: CategoryField[]
  onDeleted: (assetId: string) => void
  profile: Profile
  onClose: () => void
  onChanged: (asset: Asset) => void
  onThumbnailUpdated: (assetId: string) => void
}) {
  const [info, setInfo] = useState<DownloadInfo | null>(null)
  const [busy, setBusy] = useState(false)
  const [denial, setDenial] = useState<string | null>(null)
  const canEdit = can(profile.role, 'content.write')
  const [playerSizeRaw, setPlayerSize] = usePersistentState<PlayerSize>('detail.player-size', 'normal')
  const playerSize: PlayerSize = playerSizeRaw === 'theater' ? 'theater' : 'normal'
  const theater = asset.kind === 'video' && playerSize === 'theater'

  // ダウンロードURLは素材ごと。別の素材に切り替わったら表示を戻す
  useEffect(() => {
    setInfo(null)
    setDenial(null)
  }, [asset.id])

  // 未保存の下書き（編集タブのエディタが部位ごとに知らせる）。閉じる前に確認する
  const dirtyParts = useRef(new Set<string>())
  const markDirty = useCallback((part: string, dirty: boolean) => {
    if (dirty) dirtyParts.current.add(part)
    else dirtyParts.current.delete(part)
  }, [])
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const requestClose = useCallback(() => {
    if (dirtyParts.current.size > 0 && !window.confirm('保存していない変更があります。閉じますか？')) return
    onCloseRef.current()
  }, [])

  // ネイティブ <dialog>（SettingsDialog と同じ作り）: 背後は inert、Tab はダイアログ内に閉じ、
  // 閉じると開く前の要素（クリックしたサムネイル）へフォーカスが戻る。
  // 閉じる経路は自前（×・Esc・外側クリック → requestClose）。'close' イベントには頼らない。
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (!dialog.open) dialog.showModal()
    headingRef.current?.focus()
    const closed = () => onCloseRef.current()
    const cancel = (e: Event) => {
      e.preventDefault()
      // 拡大表示（Lightbox）が開いていればそちらだけが閉じる（Lightbox が document で拾う）
      if (document.querySelector('.lightbox')) return
      requestClose()
    }
    dialog.addEventListener('close', closed)
    dialog.addEventListener('cancel', cancel)
    return () => {
      dialog.removeEventListener('close', closed)
      dialog.removeEventListener('cancel', cancel)
      if (dialog.open) dialog.close()
    }
  }, [requestClose])
  // 外側クリックは「押し始めも外側」のときだけ。入力欄からのドラッグ選択が外で終わっても閉じない
  const downOutside = useRef(false)

  const requestDownload = async () => {
    setBusy(true)
    setDenial(null)
    try {
      const result = await getDownloadInfo(asset.id)
      setInfo(result)
    } catch (e) {
      if (e instanceof ApiError) {
        setDenial(DENIAL_MESSAGE[e.code] ?? `ダウンロードできません（${e.code}）`)
      } else {
        setDenial('ダウンロードURLの取得に失敗しました')
      }
    } finally {
      setBusy(false)
    }
  }

  const tabs: TabDef[] = [
    {
      id: 'asset-content',
      label: 'コンテンツ',
      render: () => (
        <>
          {/* 動画と写真で見せ方が違う。
              動画は透かし入りプロキシ、写真は Box から起こしたプレビュー。
              作り直しの操作は「処理と公開」タブに寄せる。 */}
          {asset.kind === 'image' ? (
            <ImageViewer asset={asset} canRegenerate={canEdit} />
          ) : (
            <ProxyPlayer asset={asset} job={job} size={playerSize} onSizeChange={setPlayerSize} />
          )}

          <dl className="meta">
            <dt>状態</dt>
            <dd>{STATUS_LABEL[asset.status] ?? asset.status}</dd>
            <dt>ファイル名</dt>
            <dd>{asset.file_name ?? '—'}</dd>
            <dt>サイズ</dt>
            <dd>{formatBytes(asset.byte_size)}</dd>
            <dt>解像度</dt>
            <dd>{asset.width && asset.height ? `${asset.width} × ${asset.height}` : '—'}</dd>
            {/* 尺は動画にしか無い。写真に「—」を並べても情報量が無いので出さない。 */}
            {asset.kind === 'video' && (
              <>
                <dt>尺</dt>
                <dd>{formatDuration(asset.duration_ms)}</dd>
              </>
            )}
            <dt>SHA-1</dt>
            <dd className="mono small">{asset.sha1 ?? '—'}</dd>
            <dt>カテゴリ</dt>
            <dd>{asset.category_id ? categoryPathLabel(categories, asset.category_id) : '未分類'}</dd>
            <dt>タグ</dt>
            <dd>{asset.tags.length ? asset.tags.join('、') : '—'}</dd>
            {asset.description && (
              <>
                <dt>説明</dt>
                <dd className="pre">{asset.description}</dd>
              </>
            )}
            {/* カスタム項目。顧客にも見える（公開情報） */}
            <AttributeRows asset={asset} categories={categories} fields={fields} assignments={assignments} />
          </dl>

          {/* サブファイル（グレースケール版・AI 加工版など）は将来ここに節を足す */}

          {!info && (
            <button className="primary" onClick={requestDownload} disabled={busy}>
              {busy ? '権利を確認中…' : '元本をダウンロード'}
            </button>
          )}
          {denial && <p className="error">{denial}</p>}

          {info && (
            <div className="download-ready">
              <p className="success">ダウンロードURLを発行しました（約15分で失効します）</p>
              <dl className="meta">
                <dt>利用範囲</dt>
                <dd>{info.usageScope}</dd>
                <dt>ダウンロード数</dt>
                <dd>
                  {info.downloadsUsed}
                  {info.maxDownloads ? ` / ${info.maxDownloads}` : ''}
                </dd>
                <dt>SHA-1（納品情報）</dt>
                <dd className="mono small">{info.sha1 ?? '—'}</dd>
              </dl>
              <a className="primary" href={info.downloadUrl} download={info.fileName ?? undefined}>
                ダウンロードを開始
              </a>
            </div>
          )}
        </>
      ),
    },
    ...(canEdit
      ? [
          {
            id: 'asset-edit',
            label: '編集',
            // 別のタブを見に行っても入力中の下書きを失わない
            keepMounted: true,
            render: () => (
              <div className="detail-edit">
                <AssetBasicsEditor
                  asset={asset}
                  onChanged={onChanged}
                  onDirtyChange={(d) => markDirty('basics', d)}
                />
                <AssetCategoryMover asset={asset} categories={categories} onChanged={onChanged} />
                <AssetAttributesEditor
                  asset={asset}
                  categories={categories}
                  fields={fields}
                  assignments={assignments}
                  onChanged={onChanged}
                  onDirtyChange={(d) => markDirty('attributes', d)}
                />
              </div>
            ),
          },
          {
            id: 'asset-manage',
            label: '処理と公開',
            render: () => (
              <StaffControls
                asset={asset}
                job={job}
                onTranscode={onTranscode}
                onDeleted={onDeleted}
                onChanged={onChanged}
                onThumbnailUpdated={onThumbnailUpdated}
              />
            ),
          },
        ]
      : []),
  ]

  return (
    <dialog
      ref={dialogRef}
      className="top-dialog detail-dialog"
      aria-labelledby="asset-detail-title"
      onPointerDown={(e) => {
        downOutside.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        const outside = downOutside.current && e.target === e.currentTarget
        downOutside.current = false
        if (outside) requestClose()
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return
        // IME 変換中の Esc は変換の取り消しであって、閉じる操作ではない
        if (e.nativeEvent.isComposing || e.keyCode === 229) return
        // 拡大表示（Lightbox）が開いていればそちらだけ閉じる（Lightbox が document で拾う）
        if (document.querySelector('.lightbox')) return
        e.preventDefault()
        requestClose()
      }}
    >
      <div className={`modal asset-detail${theater ? ' theater' : ''}`}>
        <button className="close" onClick={requestClose} aria-label="閉じる">
          ×
        </button>
        <h2 id="asset-detail-title" ref={headingRef} tabIndex={-1}>
          {asset.title}
        </h2>
        <p className="muted small detail-sub">
          {KIND_LABEL[asset.kind] ?? asset.kind} ・ {STATUS_LABEL[asset.status] ?? asset.status}
          {asset.file_name ? ` ・ ${asset.file_name}` : ''}
        </p>
        {/* 素材ごとに「コンテンツ」から開く（key で作り直す）。タブの選択は保存しない */}
        <TabbedPanel key={asset.id} tabs={tabs} label="素材の詳細" />
      </div>
    </dialog>
  )
}

/**
 * タイトル・説明・タグの編集（編集者以上）。
 *
 * 保存は PostgREST 直（RLS が content.write を持つロールに限っている）。
 * 入力中の下書きは、サーバ側の値が変わったときだけ差し替える
 * （変換完了の自動更新で同じ値が流れてきても、入力が消えないように）。
 */
const TITLE_REQUIRED = 'タイトルは必須です'

function AssetBasicsEditor({
  asset,
  onChanged,
  onDirtyChange,
}: {
  asset: Asset
  onChanged: (asset: Asset) => void
  /** 未保存の変更があるかを親へ知らせる（閉じる前の確認に使う）。 */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const signature = [asset.id, asset.title, asset.description ?? '', asset.tags.join('\u0001')].join('\u0000')
  const [title, setTitle] = useState(asset.title)
  const [description, setDescription] = useState(asset.description ?? '')
  const [tags, setTags] = useState(asset.tags.join('、'))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setTitle(asset.title)
    setDescription(asset.description ?? '')
    setTags(asset.tags.join('、'))
    setError(null)
    // 値が変わったときだけ戻す（signature に集約）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
  useEffect(() => {
    setSaved(false)
  }, [asset.id])

  const parsedTags = parseTags(tags)
  // 両側とも正規化して比べる（前後に空白のある既存タイトルで、開いた瞬間から dirty にならないように）
  const dirty =
    title.trim() !== asset.title.trim() ||
    description.trim() !== (asset.description ?? '').trim() ||
    parsedTags.join('\u0001') !== asset.tags.join('\u0001')
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  const reset = () => {
    setTitle(asset.title)
    setDescription(asset.description ?? '')
    setTags(asset.tags.join('、'))
    setError(null)
  }

  const save = async () => {
    const t = title.trim()
    if (!t) {
      setError(TITLE_REQUIRED)
      return
    }
    setBusy(true)
    setError(null)
    const { data, error } = await supabase
      .from('assets')
      .update({ title: t, description: description.trim() || null, tags: parsedTags })
      .eq('id', asset.id)
      .select('*')
      .single()
    setBusy(false)
    if (error) {
      setError(error.message)
      return
    }
    onChanged(data as Asset)
    setSaved(true)
  }

  return (
    <section className="basics-editor">
      <h3>基本情報</h3>
      <label htmlFor="basics-title">タイトル</label>
      <input
        id="basics-title"
        value={title}
        required
        aria-invalid={error === TITLE_REQUIRED || undefined}
        aria-describedby={error ? 'basics-error' : undefined}
        onChange={(e) => {
          setTitle(e.target.value)
          setSaved(false)
        }}
      />
      <label htmlFor="basics-description">説明</label>
      <textarea
        id="basics-description"
        rows={4}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value)
          setSaved(false)
        }}
      />
      <label htmlFor="basics-tags">タグ（「、」かカンマで区切る）</label>
      <input
        id="basics-tags"
        value={tags}
        placeholder="夜景、都市、空撮"
        onChange={(e) => {
          setTags(e.target.value)
          setSaved(false)
        }}
      />
      {parsedTags.length > 0 && (
        <p className="muted small basics-tags-preview">
          {parsedTags.length} 件: {parsedTags.join('、')}
        </p>
      )}
      <div className="staff-actions">
        <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>
          {busy ? '保存中…' : '基本情報を保存'}
        </button>
        {dirty && (
          <button type="button" className="link" disabled={busy} onClick={reset}>
            元に戻す
          </button>
        )}
        {saved && !dirty && (
          <span role="status" className="success small">
            保存しました
          </span>
        )}
      </div>
      {error && (
        <p id="basics-error" role="alert" className="error small">
          {error}
        </p>
      )}
    </section>
  )
}

/** 写真の詳細表示用プレビューを作り直す（「処理と公開」タブ用）。 */
function ImagePreviewRegenerator({
  asset,
  onChanged,
  onThumbnailUpdated,
}: {
  asset: Asset
  onChanged: (asset: Asset) => void
  onThumbnailUpdated: (assetId: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const run = async () => {
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      await rebuildImagePreview(asset.id)
      // 抽出はポスターだけでなくサムネイル・寸法・metadata_extracted_at も書き換え、
      // 旧サムネイルを R2 から消す。行を引き直し、一覧のサムネイルURLも取り直す
      const { data, error } = await supabase.from('assets').select('*').eq('id', asset.id).single()
      if (error) throw new Error(error.message)
      onChanged(data as Asset)
      onThumbnailUpdated(asset.id)
      setDone(true)
    } catch (e) {
      setError(describePreviewError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="staff-actions">
      <button type="button" disabled={busy} onClick={run}>
        {busy ? '作成中…' : asset.poster_r2_key ? 'プレビューを作り直す' : 'プレビューを作成する'}
      </button>
      {done && (
        <span role="status" className="success small">
          作り直しました。「コンテンツ」タブで確認できます
        </span>
      )}
      {error && (
        <span role="alert" className="error small">
          {error}
        </span>
      )}
    </div>
  )
}


/**
 * 自社スタッフ向けの公開操作。
 *
 * アップロードした素材は `processing` で登録され、**自動では公開されない**。
 * タイトル・タグ・権利表記を確認したうえでスタッフが明示的に公開する運用にしている
 * （アップロードした瞬間に顧客のカタログへ出ると事故につながるため）。
 *
 * 書き込みは RLS 側で運営スタッフ（content.write を持つロール）のみに
 * 制限されているので、ここで画面を隠すのは操作性のためであって、権限の担保はDB側にある。
 */
function StaffControls({
  asset,
  job,
  onTranscode,
  onDeleted,
  onChanged,
  onThumbnailUpdated,
}: {
  asset: Asset
  job: TranscodeJob | null
  onTranscode: (assetIds: string[], resetAttempts?: boolean) => Promise<void>
  onDeleted: (assetId: string) => void
  onChanged: (asset: Asset) => void
  onThumbnailUpdated: (assetId: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setStatus = async (status: AssetStatus) => {
    setBusy(true)
    setError(null)
    const patch: Record<string, unknown> = { status }
    // 初回公開のときだけ公開日時を入れる
    if (status === 'published' && !asset.published_at) {
      patch.published_at = new Date().toISOString()
    }
    const { data, error } = await supabase
      .from('assets')
      .update(patch)
      .eq('id', asset.id)
      .select('*')
      .single()
    setBusy(false)
    if (error) {
      setError(error.message)
      return
    }
    onChanged(data as Asset)
  }

  // 処理中・待機中は二重投入を防ぐ。ただし応答が無いまま放置された running（stale）は
  // 止まっている可能性が高く、投入側が回収してから入れ直すので、再実行の入口を残す
  const transcodeActive = job?.status === 'queued' || (job?.status === 'running' && !job.stale)

  return (
    <div className="staff-controls">
      <p className="muted small">
        自社スタッフとして閲覧中です。顧客のダウンロード可否は組織のライセンスで決まります。
      </p>
      <AssetMetadataPanel asset={asset} onChanged={onChanged} />

      <section className="manage-section">
        <h3>派生物</h3>
        <p className="muted small">
          プロキシ・サムネイル・プレビューは元本から作り直せます。透かしの設定を変えたあとは、
          プロキシを作り直すと新しい透かしが入ります。
        </p>
        {asset.kind === 'video' && (
          <div className="staff-actions">
            {/* 変換の実行。処理中・待機中は二重投入を防ぐために出さない。 */}
            {(transcodeActive || job?.stale) && (
              <TranscodeStatus job={job} hasProxy={!!asset.proxy_r2_key} />
            )}
            {!transcodeActive && (
              <button
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  setError(null)
                  try {
                    // 上限に達している場合だけ回数を戻す。
                    // 常に戻すと、二度と成功しない素材が無限に回ってしまう。
                    await onTranscode([asset.id], job?.errorCode === 'too_many_attempts')
                  } catch (e) {
                    setError(
                      `変換の投入に失敗しました（${e instanceof ApiError ? e.code : String(e)}）`,
                    )
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                {job?.errorCode === 'too_many_attempts'
                  ? '再試行の上限を解除して実行'
                  : asset.proxy_r2_key
                    ? 'プロキシを作り直す（透かしを再投入）'
                    : 'プロキシを作成する'}
              </button>
            )}
          </div>
        )}
        {asset.kind === 'image' && (
          <ImagePreviewRegenerator
            asset={asset}
            onChanged={onChanged}
            onThumbnailUpdated={onThumbnailUpdated}
          />
        )}
        <ThumbnailRegenerator
          asset={asset}
          onChanged={onChanged}
          onThumbnailUpdated={onThumbnailUpdated}
        />
      </section>

      <section className="manage-section">
        <h3>公開</h3>
        <div className="staff-actions">
          {asset.status !== 'published' && (
            <button className="primary" disabled={busy} onClick={() => setStatus('published')}>
              {busy ? '更新中…' : 'カタログに公開する'}
            </button>
          )}
          {asset.status === 'published' && (
            <button disabled={busy} onClick={() => setStatus('archived')}>
              公開を停止する
            </button>
          )}
          <AssetDeleteButton asset={asset} onDeleted={onDeleted} />
          {asset.status === 'archived' && (
            <span className="muted small">
              提供終了。ライセンスを持つ顧客には引き続き表示されます。
            </span>
          )}
        </div>
        {!asset.thumbnail_r2_key && (
          <p className="warn small">サムネイル未設定のまま公開すると一覧で見分けにくくなります。</p>
        )}
        {error && <p className="error small">{error}</p>}
      </section>
    </div>
  )
}


/**
 * サムネイルだけを差し替える。
 *
 * 元動画から1フレーム抜き直して R2 に置き、assets.thumbnail_r2_key を更新する。
 * マスターには触らないので、Box 側は一切変更されない。
 *
 * 元動画はスタッフの手元にある前提（アップロードした本人が操作する）。
 * Phase 4 の変換パイプラインが入れば、サーバ側でも生成できるようになる。
 */
function ThumbnailRegenerator({
  asset,
  onChanged,
  onThumbnailUpdated,
}: {
  asset: Asset
  onChanged: (asset: Asset) => void
  onThumbnailUpdated: (assetId: string) => void
}) {
  const [preview, setPreview] = useState<Thumbnail | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.objectUrl) }, [preview])

  const onPick = async (file: File | null) => {
    setError(null)
    setDone(false)
    if (preview) URL.revokeObjectURL(preview.objectUrl)
    setPreview(null)
    if (!file) return
    setBusy(true)
    try {
      // 黒みを避けて複数位置を試すのは generateThumbnail 側の責務
      setPreview(await generateThumbnail(file))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /** Box のプレビュー画像から作り直す。元動画は不要。 */
  const fromBox = async () => {
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      await regenerateThumbnailFromBox(asset.id)
      // 更新後の行を取り直して画面へ反映する
      const { data } = await supabase.from('assets').select('*').eq('id', asset.id).single()
      if (data) onChanged(data as Asset)
      onThumbnailUpdated(asset.id)
      setDone(true)
    } catch (e) {
      const message = e instanceof ApiError ? e.code : String(e)
      setError(
        message.startsWith('box_thumbnail_unavailable')
          ? 'Box がこの形式のプレビューを生成できませんでした。手元の動画ファイルから作成してください。'
          : `サムネイルの生成に失敗しました（${message}）`,
      )
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    if (!preview) return
    setBusy(true)
    setError(null)
    try {
      const target = await createLightUploadUrl({
        assetId: asset.id,
        kind: 'thumbnail',
        contentType: preview.contentType,
        byteSize: preview.blob.size,
      })
      await putToSignedUrl(target, preview.blob)

      const { data, error } = await supabase
        .from('assets')
        .update({ thumbnail_r2_key: target.key })
        .eq('id', asset.id)
        .select('*')
        .single()
      if (error) throw new Error(error.message)

      onChanged(data as Asset)
      onThumbnailUpdated(asset.id)
      // 古いサムネイルを R2 から掃除する（失敗しても本筋には影響しない）
      pruneThumbnails(asset.id).catch(() => undefined)
      setDone(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <details className="regen">
      <summary>サムネイルを作り直す</summary>

      <p className="muted small">
        Box が保持しているプレビュー画像から作り直します。元動画の再アップロードは不要です。
      </p>
      <button onClick={fromBox} disabled={busy}>
        {busy ? '生成中…' : 'Box から作り直す'}
      </button>

      <p className="muted small" style={{ marginTop: 16 }}>
        Box がプレビューを作れない形式（一部のコーデック）の場合は、
        手元の動画ファイルから作ることもできます。
      </p>
      <input
        type="file"
        accept="video/*,image/*"
        disabled={busy}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
      {preview && (
        <div className="selected">
          <img className="thumb-preview" src={preview.objectUrl} alt="新しいサムネイル" />
          <div>
            <p className="muted small">
              {preview.width}×{preview.height} ・ {Math.round(preview.blob.size / 1024)} KB
            </p>
            <button className="primary" onClick={apply} disabled={busy}>
              {busy ? '差し替え中…' : 'このサムネイルに差し替える'}
            </button>
          </div>
        </div>
      )}
      {done && <p className="success small">サムネイルを差し替えました。</p>}
      {error && <p className="error small">{error}</p>}
    </details>
  )
}


/**
 * 透かし入りプロキシの再生。
 *
 * R2 は非公開バケットなので、再生にも署名付きURLが要る（15分で失効）。
 * URLはクリック時に取りに行く。一覧を開いただけで全件分を発行すると、
 * 見てもいない素材の視聴ログが溜まってしまう。
 */
/**
 * 変換状況の取得。
 *
 * 進行中のジョブがある間だけ短い間隔で回し、無くなったら止める。
 * 実行中のジョブは Worker がコンテナへ問い合わせるので、
 * 漫然と回し続けるとコンテナに無駄な負荷をかける。
 */
/** 進行中があるときの間隔。 */
const ACTIVE_INTERVAL_MS = 3000
/** 進行中が無いときの間隔。画面外からの投入に気づくために止め切らない。 */
const IDLE_INTERVAL_MS = 30000
/** 問い合わせ失敗時。 */
const ERROR_INTERVAL_MS = 15000

function useTranscodeStatus(
  assetIds: string[],
  enabled: boolean,
  onCompleted: (assetIds: string[]) => void,
  revision = 0,
) {
  const [jobs, setJobs] = useState<Record<string, TranscodeJob>>({})
  // 依存配列に配列をそのまま置くと毎回再実行されるので、内容で比較する
  const key = assetIds.join(',')
  // 完了を通知済みの素材。同じ完了を何度も通知して再取得を繰り返さないため。
  const notified = useRef<Set<string>>(new Set())
  const onCompletedRef = useRef(onCompleted)
  onCompletedRef.current = onCompleted

  useEffect(() => {
    if (!enabled || !key) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const schedule = (ms: number) => {
      timer = setTimeout(tick, ms)
    }

    const tick = async () => {
      // タブが裏にある間は問い合わせない。戻ってきたら即座に追いつく。
      if (typeof document !== 'undefined' && document.hidden) {
        schedule(IDLE_INTERVAL_MS)
        return
      }
      try {
        const { jobs: rows } = await getTranscodeStatus(key.split(','))
        if (cancelled) return
        setJobs(Object.fromEntries(rows.map((j) => [j.assetId, j])))

        // 成功したものは素材を引き直してもらう。
        // ここで通知しないと proxy_r2_key が古いままで、
        // 「完了（反映待ち）」から画面が動かなくなる。
        const finished = rows
          .filter((j) => j.status === 'succeeded' && !notified.current.has(j.assetId))
          .map((j) => j.assetId)
        if (finished.length > 0) {
          finished.forEach((id) => notified.current.add(id))
          onCompletedRef.current(finished)
        }

        // 動いているものが無くなっても止め切らない。
        // 変換は画面外（別のスタッフ・CLI）からも投入されるので、
        // 完全に止めると「投入したのに何も出ない」状態から復帰できなくなる。
        schedule(hasActiveJobs(rows) ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS)
      } catch (e) {
        if (cancelled) return
        // 入力が悪い類の失敗は、何度投げても同じ。
        // 再試行し続けると「何も出ないまま裏で叩き続ける」状態になる。
        if (e instanceof ApiError && e.permanent) {
          console.error('transcode status failed permanently', e.status, e.code)
          return
        }
        // 一時的な失敗で画面を壊さない。少し置いてから再試行する
        schedule(ERROR_INTERVAL_MS)
      }
    }

    // 裏から戻ってきたら待たずに追いつく
    const onVisible = () => {
      if (cancelled || document.hidden) return
      if (timer) clearTimeout(timer)
      tick()
    }
    document.addEventListener('visibilitychange', onVisible)

    tick()

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      if (timer) clearTimeout(timer)
    }
  }, [key, enabled, revision])

  return jobs
}

/** 変換状況のバッジと進捗バー。 */
function TranscodeStatus({
  job,
  hasProxy,
  compact,
}: {
  job: TranscodeJob | null
  hasProxy: boolean
  compact?: boolean
}) {
  const d = describeTranscode(job, hasProxy)
  // 完了しているものにバッジを出すと画面が騒がしくなるだけなので出さない
  if (d.tone === 'done' && compact) return null
  if (d.tone === 'idle' && compact) return null

  return (
    <div className={`tstatus tone-${d.tone}${compact ? ' compact' : ''}`}>
      <span className="tstatus-label">{d.label}</span>
      {d.showBar && (
        <div className={`tbar${d.percent === null ? ' indeterminate' : ''}`}>
          <div className="tbar-fill" style={d.percent === null ? undefined : { width: `${d.percent}%` }} />
        </div>
      )}
      {d.tone === 'error' && job?.errorMessage && (
        <p className="muted small">{job.errorMessage}</p>
      )}
    </div>
  )
}

function ProxyPlayer({
  asset,
  job,
  size,
  onSizeChange,
}: {
  asset: Asset
  job: TranscodeJob | null
  size: PlayerSize
  onSizeChange: (size: PlayerSize) => void
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mutedFallback, setMutedFallback] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  // 詳細を開いたら自動で再生用URLを取りにいく。
  // 以前はボタンを押させていたが、サムネイルをクリックして開いた時点で
  // 「再生したい」ことは明らかなので、もう一度押させる理由が無い。
  useEffect(() => {
    setUrl(null)
    setError(null)
    setMutedFallback(false)
    if (!asset.proxy_r2_key) return

    let cancelled = false
    ;(async () => {
      try {
        const { url: signed } = await getProxyUrl(asset.id)
        if (!cancelled) setUrl(signed)
      } catch (e) {
        if (cancelled) return
        setError(
          e instanceof ApiError && e.code === 'proxy_not_ready'
            ? 'プロキシがまだ生成されていません'
            : '再生用URLを取得できませんでした',
        )
      }
    })()
    return () => {
      cancelled = true
    }
  }, [asset.id, asset.proxy_r2_key])

  /**
   * 自動再生を試みる。
   *
   * ブラウザは音声付きの自動再生を原則ブロックする。クリックで開いた直後なので
   * 通ることも多いが、URL取得の非同期を挟むため確実ではない（Safari は特に厳しい）。
   * 音声付きで試し、拒否されたらミュートで再生して「音声を出す」導線を出す。
   * 黙ってミュートで流すと「音が出ない」という別の問い合わせになる。
   */
  const startPlayback = async () => {
    const el = videoRef.current
    if (!el) return
    try {
      el.muted = false
      await el.play()
      setMutedFallback(false)
    } catch {
      try {
        el.muted = true
        await el.play()
        setMutedFallback(true)
      } catch {
        // 完全に拒否された場合は controls から手動で再生してもらう
      }
    }
  }

  const unmute = async () => {
    const el = videoRef.current
    if (!el) return
    el.muted = false
    setMutedFallback(false)
    await el.play().catch(() => undefined)
  }

  /** 全画面はブラウザに任せる。iOS Safari は video 専用の API しか無い。 */
  const enterFullscreen = () => {
    const el = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null
    if (!el) return
    if (typeof el.requestFullscreen === 'function') {
      void el.requestFullscreen().catch(() => undefined)
    } else {
      el.webkitEnterFullscreen?.()
    }
  }

  if (!asset.proxy_r2_key) {
    // 「未生成です」だけだと、待てば終わるのか放置されているのかが分からない。
    // 変換中なら段階と進捗を出す。
    return (
      <div className="player-placeholder">
        <TranscodeStatus job={job} hasProxy={false} />
        {!job && (
          <p className="muted small">
            スタッフが変換を実行すると再生できるようになります。
          </p>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className="player-placeholder">
        <p className="error small">{error}</p>
      </div>
    )
  }

  if (!url) {
    return (
      <div className="player-placeholder">
        <p className="muted small">読み込み中…</p>
      </div>
    )
  }

  return (
    <div className="player-wrap">
      <div className="player">
        {/* 透かしが焼き込まれているので、画面録画されても出所を追跡できる */}
        <video
          ref={videoRef}
          src={url}
          controls
          autoPlay
          playsInline
          preload="auto"
          onLoadedMetadata={startPlayback}
        />
      </div>
      <div className="player-bar">
        {mutedFallback ? (
          <button className="link unmute" onClick={unmute}>
            音声を出す（ブラウザの制限で消音で再生されています）
          </button>
        ) : (
          <span />
        )}
        {/* 標準 → 拡大（モーダルごと広がる中間サイズ）→ 全画面 の3段階 */}
        <div className="view-switch player-size" role="group" aria-label="表示サイズ">
          <button
            type="button"
            className={size === 'normal' ? 'active' : undefined}
            aria-pressed={size === 'normal'}
            onClick={() => onSizeChange('normal')}
          >
            標準
          </button>
          <button
            type="button"
            className={`theater-btn${size === 'theater' ? ' active' : ''}`}
            aria-pressed={size === 'theater'}
            onClick={() => onSizeChange('theater')}
          >
            拡大
          </button>
          <button type="button" onClick={enterFullscreen}>
            全画面
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * カテゴリの移動。
 *
 * **Box 側のフォルダも動く。** カテゴリは Box フォルダと1:1で対応しているため、
 * DB だけ書き換えると「カタログ上はA、実体はBのフォルダ」というずれが残る。
 * Worker が Box の移動を済ませてから DB を更新する。
 */
function AssetCategoryMover({
  asset,
  categories,
  onChanged,
}: {
  asset: Asset
  categories: Category[]
  onChanged: (asset: Asset) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const move = async (categoryId: string) => {
    if (categoryId === asset.category_id) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await moveAssetCategory(asset.id, categoryId)
      const { data } = await supabase.from('assets').select('*').eq('id', asset.id).single()
      if (data) onChanged(data as Asset)
      setNotice('Box のフォルダも移動しました')
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e)
      setError(
        code === 'category_not_leaf'
          ? '子カテゴリを持つカテゴリには置けません。末端のカテゴリを選んでください'
          : `移動に失敗しました（${code}）`,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="asset-move">
      <label htmlFor={`move-${asset.id}`} className="small">
        カテゴリ
      </label>
      <select
        id={`move-${asset.id}`}
        value={asset.category_id ?? ''}
        disabled={busy}
        onChange={(e) => move(e.target.value)}
      >
        {/* 素材を置けるのは末端だけ。枝は選択肢に出さず、経路で区別する */}
        {asset.category_id === null && (
          <option value="" disabled>
            未分類
          </option>
        )}
        {leafCategories(categories).map((c) => (
          <option key={c.id} value={c.id}>
            {categoryPathLabel(categories, c.id)}
          </option>
        ))}
      </select>
      {busy && <span className="muted small">移動中…</span>}
      {notice && <span className="success small">{notice}</span>}
      {error && <span className="error small">{error}</span>}
    </div>
  )
}

/**
 * メタデータの表示と再取得。
 *
 * 撮影地はスタッフにしか見えない別テーブル（asset_private_metadata）にある。
 * 顧客が assets を select したときに位置情報が混ざらないようにするため。
 */
function AssetMetadataPanel({
  asset,
  onChanged,
}: {
  asset: Asset
  onChanged: (asset: Asset) => void
}) {
  const [priv, setPriv] = useState<{
    gps_lat: number | null
    gps_lon: number | null
    exif: Record<string, unknown>
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    supabase
      .from('asset_private_metadata')
      .select('gps_lat,gps_lon,exif')
      .eq('asset_id', asset.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return
        // 失敗を握り潰すと「撮影地 —」が権限不足なのか未取得なのか分からなくなる
        if (error) {
          setError(`撮影地の取得に失敗しました（${error.message}）`)
          return
        }
        setPriv(data as typeof priv)
      })
    return () => {
      cancelled = true
    }
  }, [asset.id])

  const extract = async () => {
    setBusy(true)
    setError(null)
    try {
      await extractMetadata(asset.id)
      const [a, p] = await Promise.all([
        supabase.from('assets').select('*').eq('id', asset.id).single(),
        supabase
          .from('asset_private_metadata')
          .select('gps_lat,gps_lon,exif')
          .eq('asset_id', asset.id)
          .maybeSingle(),
      ])
      if (a.error) throw new Error(a.error.message)
      if (p.error) throw new Error(p.error.message)
      if (a.data) onChanged(a.data as Asset)
      setPriv(p.data as typeof priv)
    } catch (e) {
      setError(`取得に失敗しました（${e instanceof ApiError ? e.code : String(e)}）`)
    } finally {
      setBusy(false)
    }
  }

  const exif = (priv?.exif ?? {}) as Record<string, unknown>
  const hasGps = priv?.gps_lat != null && priv?.gps_lon != null

  return (
    <details className="metadata-panel">
      <summary>メタデータ{asset.metadata_extracted_at ? '' : '（未取得）'}</summary>
      <dl className="meta">
        <dt>撮影日時</dt>
        <dd>{asset.captured_at ? new Date(asset.captured_at).toLocaleString('ja-JP') : '—'}</dd>
        <dt>機材</dt>
        <dd>
          {[exif.make, exif.model].filter(Boolean).join(' ') || '—'}
          {exif.lens ? `／${exif.lens}` : ''}
        </dd>
        <dt>露出</dt>
        <dd>
          {[
            exif.focalLengthMm ? `${exif.focalLengthMm}mm` : null,
            exif.fNumber ? `F${exif.fNumber}` : null,
            exif.exposureTime ? `${exif.exposureTime}s` : null,
            exif.iso ? `ISO${exif.iso}` : null,
          ]
            .filter(Boolean)
            .join(' ') || '—'}
        </dd>
        <dt>撮影地</dt>
        <dd>
          {hasGps ? (
            <>
              <span className="mono small">
                {priv!.gps_lat!.toFixed(5)}, {priv!.gps_lon!.toFixed(5)}
              </span>{' '}
              <a
                href={`https://www.google.com/maps?q=${priv!.gps_lat},${priv!.gps_lon}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                地図
              </a>
              <span className="muted small">（スタッフのみ表示）</span>
            </>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      <button disabled={busy} onClick={extract}>
        {busy ? '取得中…' : asset.metadata_extracted_at ? 'メタデータを取り直す' : 'メタデータを取得する'}
      </button>
      {error && <p className="error small">{error}</p>}
    </details>
  )
}

/**
 * 素材の削除。
 *
 * Box はゴミ箱へ送る（既定30日は復元可）。R2 の派生物は削除する。
 * **監査ログは残る** — 誰が何を落としたかの記録は素材の存否と無関係に保つ。
 *
 * 有効なライセンスがある素材はサーバ側で拒否される。契約の裏付けがあるものを
 * 画面操作ひとつで消せてしまうのは事故なので、そこは緩めない。
 */
function AssetDeleteButton({
  asset,
  onDeleted,
}: {
  asset: Asset
  onDeleted: (assetId: string) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setBusy(true)
    setError(null)
    try {
      await deleteAsset(asset.id)
      onDeleted(asset.id)
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e)
      setError(
        code === 'has_active_licenses'
          ? 'この素材には有効なライセンスがあります。先にライセンスを失効させてください。'
          : `削除に失敗しました（${code}）`,
      )
      setBusy(false)
    }
  }

  if (!confirming) {
    return (
      <button className="danger" onClick={() => setConfirming(true)}>
        削除する
      </button>
    )
  }

  return (
    <div className="delete-confirm">
      <p className="small">
        <strong>{asset.title}</strong> を削除します。カタログから消え、R2 のプロキシとサムネイルも
        削除されます。Box の元本は<strong>ゴミ箱へ移動</strong>し、既定で30日間は Box 側で復元できます。
        ダウンロードの監査ログは残ります。
      </p>
      <p className="small">
        確認のため <code>削除</code> と入力してください。
      </p>
      <input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder="削除"
        aria-label="確認文字列"
      />
      <div className="staff-actions">
        <button className="danger" disabled={busy || typed !== '削除'} onClick={run}>
          {busy ? '削除中…' : '削除を実行する'}
        </button>
        <button
          className="link"
          disabled={busy}
          onClick={() => {
            setConfirming(false)
            setTyped('')
            setError(null)
          }}
        >
          やめる
        </button>
      </div>
      {error && <p className="error small">{error}</p>}
    </div>
  )
}

/**
 * 写真の表示。
 *
 * 動画には透かし入りプロキシがあるが、写真には無い。
 * 一覧のサムネイル（長辺640px）は吟味するには小さいので、
 * Box が持っている representations の 2048px を R2 に起こして表示する。
 *
 * **元本は出さない。** 素材本体のダウンロードはライセンス検査を通る
 * 経路（/v1/assets/:id/download-url）だけが発行する。ここは閲覧用。
 */
function ImageViewer({
  asset,
  canRegenerate,
}: {
  asset: Asset
  /** プレビューが無いときに自動で作る権限があるか（作り直しの操作は「処理と公開」タブにある）。 */
  canRegenerate: boolean
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [fallback, setFallback] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setFallback(null)
    setExpanded(false)
    ;(async () => {
      // プレビューが無ければサムネイルで代用する。
      // 小さくても「何も出ない」よりはるかにましなので、必ず何かを出す。
      const [preview, thumb] = await Promise.all([
        getPreviewUrls([asset.id]).catch(() => ({ urls: {} as Record<string, string> })),
        asset.thumbnail_r2_key
          ? getThumbnailUrls([asset.id]).catch(() => ({ urls: {} as Record<string, string> }))
          : Promise.resolve({ urls: {} as Record<string, string> }),
      ])
      if (cancelled) return
      setUrl(preview.urls[asset.id] ?? null)
      setFallback(thumb.urls[asset.id] ?? null)

      // プレビューが無ければ、スタッフが開いた時点で作っておく。
      //
      // 顧客には作成手段が無いので、放っておくと 640px のサムネイルを
      // 見せ続けることになる。Box の representations を1枚もらうだけの
      // 冪等な操作なので、開いたついでに済ませる。
      if (!preview.urls[asset.id] && canRegenerate && asset.box_file_id) {
        setBusy(true)
        try {
          // メタデータ抽出と同じ経路で作る。元本のダウンロードを共有できるうえ、
          // Box の representations と違ってファイル単位で失敗しない。
          await extractMetadata(asset.id)
          if (!cancelled) {
            const again = await getPreviewUrls([asset.id])
            if (!cancelled) setUrl(again.urls[asset.id] ?? null)
          }
        } catch {
          // ffmpeg が読めない形式もある。サムネイル表示のまま続ける。
        } finally {
          if (!cancelled) setBusy(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [asset.id, asset.thumbnail_r2_key, asset.box_file_id, canRegenerate])

  const shown = url ?? fallback

  return (
    <div className="image-viewer">
      {shown ? (
        <button
          type="button"
          className="image-open"
          onClick={() => setExpanded(true)}
          aria-label={`${asset.title} を拡大表示`}
        >
          <img src={shown} alt={asset.title} draggable={false} />
          <span className="image-open-hint">クリックで拡大</span>
        </button>
      ) : (
        <div className="player-placeholder">
          <p className="muted small">
            表示できる画像がありません。
            {canRegenerate
              ? '「処理と公開」タブの「プレビューを作成する」で作成できます。'
              : '担当者にお問い合わせください。'}
          </p>
        </div>
      )}

      {shown && !url && (
        <p className="muted small">
          {busy
            ? '詳細表示用のプレビューを作成しています…'
            : '一覧用のサムネイルを表示しています（実寸より小さい画像です）。'}
        </p>
      )}


      {expanded && shown && (
        <Lightbox src={shown} alt={asset.title} onClose={() => setExpanded(false)} />
      )}
    </div>
  )
}


/** 絞り込みの1項目ぶん。値が多いときは畳む（撮影場所のような自由記述は際限なく増える）。 */
const FACET_SHOW = 10

function FacetGroup({
  field,
  values,
  selected,
  onToggle,
}: {
  field: AssetField
  values: { value: string; label: string; count: number }[]
  selected: string[]
  onToggle: (value: string, on: boolean) => void
}) {
  const [showAll, setShowAll] = useState(false)
  const chosen = new Set(selected)
  // 件数 0 でも選択中の値は残す（外す手段が無くなる）
  const usable = values.filter((v) => v.count > 0 || chosen.has(v.value))
  if (usable.length === 0) return null
  const shown = showAll ? usable : usable.filter((v, i) => i < FACET_SHOW || chosen.has(v.value))
  const hidden = usable.length - shown.length
  return (
    <div className="facet">
      <h3 className="facet-title">{field.label}</h3>
      {shown.map((v) => (
        <SidebarCheckItem
          key={v.value}
          checked={chosen.has(v.value)}
          count={v.count}
          onChange={(on) => onToggle(v.value, on)}
        >
          {v.label}
        </SidebarCheckItem>
      ))}
      {(hidden > 0 || showAll) && (
        <button type="button" className="link" onClick={() => setShowAll((x) => !x)}>
          {showAll ? '少なく表示' : `ほか ${hidden} 件を表示`}
        </button>
      )}
    </div>
  )
}

/**
 * 詳細のメタ表に並べるカスタム項目の行。dl の中に置くので dt/dd の断片を返す。
 * 値が無い項目も「—」で出す（何が入力できるかが分かる）。
 * 今のカテゴリに無い項目の値（別カテゴリから移ってきた素材）は薄く出す。値は保持されている。
 */
function AttributeRows({
  asset,
  categories,
  fields,
  assignments,
}: {
  asset: Asset
  categories: Category[]
  fields: AssetField[]
  assignments: CategoryField[]
}) {
  const effective = effectiveFields(asset.category_id, categories, fields, assignments)
  const known = new Set(effective.map((f) => f.key))
  const byKey = new Map(fields.map((f) => [f.key, f]))
  const orphans = Object.keys(asset.attributes ?? {}).filter((k) => !known.has(k))
  return (
    <>
      {effective.map((f) => {
        const v = formatFieldValue(f, asset.attributes?.[f.key])
        return (
          <Fragment key={f.key}>
            <dt>{f.label}</dt>
            <dd className={v === null ? 'attr-unset' : undefined}>{v ?? '—'}</dd>
          </Fragment>
        )
      })}
      {orphans.map((k) => {
        const f = byKey.get(k)
        const raw = asset.attributes[k]
        const v = f ? formatFieldValue(f, raw) : Array.isArray(raw) ? raw.join('、') : String(raw)
        return (
          <Fragment key={k}>
            <dt className="attr-inherited">{f?.label ?? k}</dt>
            <dd className="attr-inherited">
              {v ?? '—'} <span className="small">（他カテゴリの項目）</span>
            </dd>
          </Fragment>
        )
      })}
    </>
  )
}

const normalizeEmpty = (v: unknown): unknown =>
  v === '' || v === undefined || (Array.isArray(v) && v.length === 0) ? null : v
const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(normalizeEmpty(a)) === JSON.stringify(normalizeEmpty(b))

/** Worker の invalid_attributes:key=reason,... を人に読める形にする。 */
function describeAttributeErrors(code: string, effective: EffectiveField[]): string {
  const reasons: Record<string, string> = {
    type: '型が合いません',
    format: '日付は YYYY-MM-DD の形にしてください',
    option: '選択肢にありません',
    not_applicable: 'このカテゴリの項目ではありません',
  }
  return code
    .slice('invalid_attributes:'.length)
    .split(',')
    .map((pair) => {
      const [key, reason] = pair.split('=')
      const label = effective.find((f) => f.key === key)?.label ?? key
      return `${label}: ${reasons[reason ?? ''] ?? reason}`
    })
    .join('、')
}

/**
 * カスタム項目の編集（スタッフ）。
 *
 * 何を入力できるかはカテゴリへの割り当てで決まり（設定 › カテゴリ）、ここは値を入れるだけ。
 * 保存は Worker 経由（型と選択肢を検証する）。空にしたキーは消える。
 * 別カテゴリから移ってきた素材の「今のカテゴリに無い項目」はここでは触らない（値は保持される）。
 */
function AssetAttributesEditor({
  asset,
  categories,
  fields,
  assignments,
  onChanged,
  onDirtyChange,
}: {
  asset: Asset
  categories: Category[]
  fields: AssetField[]
  assignments: CategoryField[]
  onChanged: (asset: Asset) => void
  /** 未保存の変更があるかを親へ知らせる（閉じる前の確認に使う）。 */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const effective = useMemo(
    () => effectiveFields(asset.category_id, categories, fields, assignments),
    [asset.category_id, categories, fields, assignments],
  )
  // 値が変わったときだけ initial を作り直す。行の引き直しで attributes の参照が変わっても
  // 値が同じなら下書きを消さない（変換完了の自動更新・基本情報の保存で入力が飛んでいた）
  const signature = useMemo(
    () => JSON.stringify(effective.map((f) => [f.key, asset.attributes?.[f.key] ?? null])),
    [effective, asset.attributes],
  )
  const initial = useMemo(
    () => Object.fromEntries(JSON.parse(signature) as [string, unknown][]) as Record<string, unknown>,
    [signature],
  )
  const [draft, setDraft] = useState<Record<string, unknown>>(initial)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // 別の素材を開いたとき・保存で値が更新されたときに入力欄を合わせる。
  // 「保存しました」はここで消さない（保存直後の更新でも initial が変わるため、
  // 消すと表示される前に消える）。別の素材に切り替わったときだけ消す。
  useEffect(() => {
    setDraft(initial)
  }, [initial])
  useEffect(() => {
    setSaved(false)
    setError(null)
  }, [asset.id])

  const dirty = effective.some((f) => !sameValue(draft[f.key], initial[f.key]))
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])
  const set = (key: string, value: unknown) => {
    setDraft((d) => ({ ...d, [key]: value }))
    setSaved(false)
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const values: Record<string, unknown> = {}
      for (const f of effective) {
        if (!sameValue(draft[f.key], initial[f.key])) values[f.key] = draft[f.key] ?? null
      }
      await updateAssetAttributes(asset.id, values)
      const { data } = await supabase.from('assets').select('*').eq('id', asset.id).single()
      if (data) onChanged(data as Asset)
      setSaved(true)
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e)
      setError(
        code.startsWith('invalid_attributes:')
          ? `入力に誤りがあります（${describeAttributeErrors(code, effective)}）`
          : `保存できませんでした（${code}）`,
      )
    } finally {
      setBusy(false)
    }
  }

  if (!asset.category_id) {
    return <p className="muted small">カテゴリを設定すると項目を入力できます。</p>
  }
  if (effective.length === 0) {
    return (
      <p className="muted small">
        このカテゴリには項目が割り当てられていません（設定 › カテゴリ で割り当てられます）。
      </p>
    )
  }

  return (
    <details className="attr-editor" open>
      <summary>項目を編集</summary>
      <div className="attr-grid">
        {effective.map((f) => (
          <Fragment key={f.key}>
            {/* multi_enum はチェックボックス群なので label の対象にできない。id で group に結び付ける */}
            <label id={`attr-${f.key}-label`} htmlFor={f.type === 'multi_enum' ? undefined : `attr-${f.key}`}>
              {f.label}
              {f.required && <span className="warn"> *</span>}
            </label>
            <AttributeInput id={`attr-${f.key}`} field={f} value={draft[f.key]} onChange={(v) => set(f.key, v)} />
            {f.description && <p className="attr-hint">{f.description}</p>}
          </Fragment>
        ))}
      </div>
      <div className="staff-actions">
        <button type="button" className="primary" disabled={busy || !dirty} onClick={save}>
          {busy ? '保存中…' : '項目を保存'}
        </button>
        {dirty && (
          <button type="button" className="link" disabled={busy} onClick={() => setDraft(initial)}>
            元に戻す
          </button>
        )}
        {saved && !dirty && <span className="success small">保存しました</span>}
      </div>
      {error && <p className="error small">{error}</p>}
    </details>
  )
}

function AttributeInput({
  id,
  field,
  value,
  onChange,
}: {
  id: string
  field: EffectiveField
  value: unknown
  onChange: (v: unknown) => void
}) {
  switch (field.type) {
    case 'text':
      return <input id={id} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
    case 'number':
      return (
        <input
          id={id}
          type="number"
          step="any"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      )
    case 'date':
      return (
        <input
          id={id}
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      )
    case 'boolean':
      return (
        <select
          id={id}
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
        >
          <option value="">（未設定）</option>
          <option value="true">はい</option>
          <option value="false">いいえ</option>
        </select>
      )
    case 'enum':
      return (
        <select id={id} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">（未設定）</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )
    case 'multi_enum': {
      const current = Array.isArray(value) ? (value as string[]) : []
      return (
        <div className="attr-multi" id={id} role="group" aria-labelledby={`${id}-label`}>
          {field.options.map((o) => (
            <label key={o.value}>
              <input
                type="checkbox"
                checked={current.includes(o.value)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...current, o.value] : current.filter((v) => v !== o.value))
                }
              />
              {o.label}
            </label>
          ))}
          {field.options.length === 0 && <span className="muted small">選択肢がありません</span>}
        </div>
      )
    }
  }
}
